import React, { useState, useEffect } from 'react';
import { Course } from '../types';
import {
  Box,
  Button,
  Container,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
} from '@mui/material';

interface CourseInputProps {
  onTimetableGenerated: (timetable: any) => void;
}

const CourseInput: React.FC<CourseInputProps> = ({ onTimetableGenerated }) => {
  const [formData, setFormData] = useState({
    courseName: '',
    teacherId: 'T1',
    roomId: 'R1',
    preferredDay: 'Monday',
    preferredTime: 'Morning',
    duration: 1.5
  });

  const [courses, setCourses] = useState<Course[]>([]);
  const [showTimetable, setShowTimetable] = useState(false);
  const [isBackendConnected, setIsBackendConnected] = useState(true);

  useEffect(() => {
    testBackendConnection();
  }, []);

  const testBackendConnection = async () => {
    try {
      const response = await fetch('https://timetable-demo-api.01tech.work/api/test');
      const data = await response.json();
      setIsBackendConnected(true);
      console.log('Backend connection test:', data);
    } catch (error) {
      console.error('Backend connection test failed:', error);
      setIsBackendConnected(false);
    }
  };

  const getCoursesForDay = (day: string) => {
    return courses.filter(course => course.preferredDay === day).length;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Check if the selected day already has 6 courses
    const coursesForDay = getCoursesForDay(formData.preferredDay);
    if (coursesForDay >= 6) {
      alert(`Cannot add more courses for ${formData.preferredDay}. Maximum 6 courses per day allowed.`);
      return;
    }

    const newCourse: Course = {
      id: `C${courses.length + 1}`,
      name: formData.courseName,
      teacherId: formData.teacherId,
      roomId: 'R1',
      preferredDay: formData.preferredDay,
      preferredTime: formData.preferredTime,
      duration: 1.5
    };

    setCourses([...courses, newCourse]);
    setFormData({
      courseName: '',
      teacherId: 'T1',
      roomId: 'R1',
      preferredDay: 'Monday',
      preferredTime: 'Morning',
      duration: 1.5
    });
    setShowTimetable(false);
  };

  const handleClearCourses = () => {
    setCourses([]);
    setShowTimetable(false);
    localStorage.removeItem('timetable');
    localStorage.removeItem('unmetPreferences');
    onTimetableGenerated({ timetable: [], unmetPreferences: [] });
  };

  const handleGenerateTimetable = async () => {
    if (!isBackendConnected) {
      alert('Cannot connect to the backend server. Please make sure it is running.');
      return;
    }

    if (courses.length === 0) {
      alert('Please add at least one course before generating the timetable.');
      return;
    }

    try {
      console.log('Sending courses to backend:', courses);
      const response = await fetch('https://timetable-demo-api.01tech.work/api/generate-timetable', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          courses,
          teachers: [
            { id: 'T1', name: 'Teacher 1' },
            { id: 'T2', name: 'Teacher 2' },
            { id: 'T3', name: 'Teacher 3' }
          ],
          rooms: [
            { id: 'R1', name: 'Room 1', capacity: 30 }
          ]
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log('Received response from backend:', data);
      
      if (data.success) {
        localStorage.setItem('timetable', JSON.stringify(data.timetable));
        localStorage.setItem('unmetPreferences', JSON.stringify(data.unmetPreferences));
        onTimetableGenerated(data);
        setShowTimetable(true);
      } else {
        alert(data.message || 'Failed to generate timetable');
      }
    } catch (error) {
      console.error('Error generating timetable:', error);
      alert('Failed to connect to the server. Please try again later.');
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 0 }}>
      {!isBackendConnected && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Warning: Cannot connect to the backend server. Please make sure it is running.
        </Alert>
      )}

      <Box sx={{ mb: 4 }}>
        {/* <Typography variant="h6" sx={{ mb: 2 }}>
          Course Timetabling System Demo
        </Typography> */}
        <Typography variant="body1" sx={{ mb: 2 }}>
          This is a demo of a course timetabling system with the following constraints:
        </Typography>
        <Box component="ul" sx={{ mb: 2 }}>
          <Box component="li" sx={{ mb: 1 }}>
            <Typography variant="body2">
              • Single room available for all courses
            </Typography>
          </Box>
          <Box component="li" sx={{ mb: 1 }}>
            <Typography variant="body2">
              • Each course duration is fixed at 1.5 hours
            </Typography>
          </Box>
          <Box component="li" sx={{ mb: 1 }}>
            <Typography variant="body2">
              • Maximum 6 courses per day (one per time slot)
            </Typography>
          </Box>
          <Box component="li" sx={{ mb: 1 }}>
            <Typography variant="body2">
              • Time slots: 09:00-10:30, 10:45-12:15, 13:30-15:00, 15:15-16:45, 17:00-18:30, 18:45-20:15
            </Typography>
          </Box>
          <Box component="li">
            <Typography variant="body2">
              • System will try to schedule courses in their preferred time slots when possible
            </Typography>
          </Box>
        </Box>
      </Box>

      <Paper elevation={3} sx={{ p: 3, mb: 4 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Add a New Course
        </Typography>
        <form onSubmit={handleSubmit}>
          <Box sx={{ display: 'grid', gap: 2 }}>
            <TextField
              label="Course Name"
              value={formData.courseName}
              onChange={(e) => setFormData({ ...formData, courseName: e.target.value })}
              required
              fullWidth
            />

            <FormControl fullWidth>
              <InputLabel>Teacher</InputLabel>
              <Select
                value={formData.teacherId}
                label="Teacher"
                onChange={(e) => setFormData({ ...formData, teacherId: e.target.value })}
              >
                <MenuItem value="T1">Teacher 1</MenuItem>
                <MenuItem value="T2">Teacher 2</MenuItem>
                <MenuItem value="T3">Teacher 3</MenuItem>
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>Preferred Day</InputLabel>
              <Select
                value={formData.preferredDay}
                label="Preferred Day"
                onChange={(e) => setFormData({ ...formData, preferredDay: e.target.value })}
              >
                <MenuItem value="Monday">Monday ({getCoursesForDay('Monday')}/6)</MenuItem>
                <MenuItem value="Tuesday">Tuesday ({getCoursesForDay('Tuesday')}/6)</MenuItem>
                <MenuItem value="Wednesday">Wednesday ({getCoursesForDay('Wednesday')}/6)</MenuItem>
                <MenuItem value="Thursday">Thursday ({getCoursesForDay('Thursday')}/6)</MenuItem>
                <MenuItem value="Friday">Friday ({getCoursesForDay('Friday')}/6)</MenuItem>
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>Preferred Time</InputLabel>
              <Select
                value={formData.preferredTime}
                label="Preferred Time"
                onChange={(e) => setFormData({ ...formData, preferredTime: e.target.value })}
              >
                <MenuItem value="Morning">Morning (9:00-12:15)</MenuItem>
                <MenuItem value="Afternoon">Afternoon (13:30-16:45)</MenuItem>
                <MenuItem value="Evening">Evening (17:00-20:45)</MenuItem>
              </Select>
            </FormControl>

            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'space-between' }}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={getCoursesForDay(formData.preferredDay) >= 6}
              >
                Add Course
              </Button>
              <Button
                onClick={handleGenerateTimetable}
                variant="contained"
                color="success"
              >
                {showTimetable ? 'Regenerate Timetable' : 'Generate Timetable'}
              </Button>
            </Box>
          </Box>
        </form>
      </Paper>

      {courses.length > 0 && (
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h5">
              Added Courses ({courses.length})
            </Typography>
            <Button
              onClick={handleClearCourses}
              variant="outlined"
              color="error"
            >
              Clear All Courses
            </Button>
          </Box>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Course</TableCell>
                  <TableCell>Teacher</TableCell>
                  <TableCell>Preferred Day</TableCell>
                  <TableCell>Preferred Time</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {courses.map((course) => (
                  <TableRow key={course.id}>
                    <TableCell>{course.name}</TableCell>
                    <TableCell>Teacher {course.teacherId.slice(1)}</TableCell>
                    <TableCell>{course.preferredDay}</TableCell>
                    <TableCell>{course.preferredTime}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}
    </Container>
  );
};

export default CourseInput; 